import { intersection, isEqual, isObject } from 'lodash-es';
const LangKeys = ['hk', 'en', 'cn'];
const DefaultLangKey = 'hk';

export type OriginalLangKey = 'en' | 'zh-hk' | 'zh-cn';

export type LangKey = 'hk' | 'en' | 'cn';

export type LangConfig = {
  [K in LangKey]: K extends 'hk' ? string : string | undefined;
};

type FormattedLangConfig<T> = {
  [K in keyof T]: T[K] extends LangConfig
    ? string
    : T[K] extends (infer U)[]
      ? FormattedLangConfig<U>[]
      : T[K] extends object
        ? FormattedLangConfig<T[K]>
        : T[K];
};

/**
 * 根据当前环境语言('en' | 'zh-hk' | 'zh-cn'),获取对应配置
 * 多语言对象格式参考：
		{
			"cn": "简体",
			"en": "English",
			"hk": "繁体"
		}
 * @param input object
 * @returns 多语言格式化后的对象 object
 */
export function getConfigWithLang<T>(obj: T | T[] | Array<LangConfig>, originalLang: OriginalLangKey):
FormattedLangConfig<T> | FormattedLangConfig<T>[] | T | string[] {
  if (!isObject(obj) || obj instanceof Date) {
    return obj as T;
  }

  const langKey = getKeyNameWithLang(originalLang);

  if (Array.isArray(obj)) {
    if (obj.length > 0 && checkValidLangConfig(obj[0])) {
      return (obj as Array<LangConfig>).map(item => getValueByTargetLangKey(item as object, langKey)) as string[];
    }

    return (obj as T[]).map(item => getConfigWithLang(item, originalLang)) as FormattedLangConfig<T>[];
  }

  const result: any = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in obj as unknown as object) {
    // eslint-disable-next-line no-prototype-builtins
    if ((obj as unknown as object).hasOwnProperty(key)) {
      const value = obj[key as keyof T];

      if (value && typeof value === 'object') {
        if (Array.isArray(value)) {
          result[key] = (value as object[]).map(item => getConfigWithLang(item, originalLang));
        } else if (checkValidLangConfig(value)) {
          result[key] = getValueByTargetLangKey(value, langKey);
        } else {
          result[key] = getConfigWithLang(value, originalLang);
        }
      } else {
        result[key] = value;
      }
    }
  }

  return result as FormattedLangConfig<T>;
}

/** 根据源多语言返回目标多语言字段 */
const getKeyNameWithLang = (lang: OriginalLangKey) => {
  const newKey = (lang as string).split('-')
    .reverse()[0];
  return newKey as LangKey;
};

/** 根据多语言key获取对应值，如果为空则获取兜底多语言key对应的值 */
function getValueByTargetLangKey<T>(value: T | T[], langKey: LangKey): T | string {
  return value[langKey] || value[DefaultLangKey];
}

/** 检查是否为多语言配置对象:
 * 1. 该对象是否有兜底LangKey属性
 * 2. 该对象的所有属性是否都为LangKey
 */
function checkValidLangConfig<T>(obj: T | T[]): boolean {
  if (!isObject(obj)) {
    return false;
  }
  const objKeys = Object.keys(obj as object);
  const isLangKeysSubset = isEqual(intersection(objKeys, LangKeys), objKeys);
  const hasDefaultLangKey = objKeys.includes(DefaultLangKey);
  return isLangKeysSubset && hasDefaultLangKey;
};
